import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import LogoMark from "@/components/LogoMark";

export default function Privacy() {
  return (
    <div
      data-testid="privacy-page"
      className="min-h-screen w-full"
      style={{ background: "var(--un-bg-1)" }}
    >
      <motion.main
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-[680px] mx-auto px-5 py-12 sm:py-16"
      >
        {/* Kicker */}
        <div className="flex items-center gap-2 mb-5">
          <LogoMark size={34} title="" />
          <p className="text-[11px] tracking-[0.28em] uppercase" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
            UnAnonymous
          </p>
        </div>

        <h1
          className="text-[34px] sm:text-[40px] leading-[1.05] mb-2"
          style={{
            color: "var(--un-text-1)",
            fontFamily: "'Lora', serif",
            fontWeight: 500,
            letterSpacing: "-0.005em",
          }}
        >
          Privacy Policy
        </h1>

        <p
          className="text-[14px] italic"
          style={{
            color: "var(--un-text-3)",
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "17px",
          }}
        >
          Last updated: October 5, 2026
        </p>

        <Section>
          <Body>
            UnAnonymous is designed to be private by default. Core use does
            not require an account. If you create one, account identity and
            minimal account state are kept separate from sensitive reflections,
            which are designed to stay on your device unless you deliberately
            use a feature that needs external processing, sharing, or a future
            sync feature you explicitly enable.
          </Body>
        </Section>

        <Section heading="What stays on your device">
          <List
            items={[
              "Emotional check-in results",
              "Reflection journal entries",
              "Mosaic items (quotes, photos, songs, messages, reminders)",
              "Tomorrow-you letters and Living Book writing",
              "Drafts on hold",
              "Protect list",
              "Nudge preferences and your phrase library",
              "Gentle rhythm history, alignment notes, Peace Compass, Safer Tonight plans and Look Forward items",
            ]}
          />
          <Body>
            All of the above are stored on your device only. Mosaic items and
            tomorrow-you letters are additionally encrypted at rest where supported. Living Book writing requires encrypted storage. Encryption keys remain on your device; this is not a password lock. The app does not automatically upload this content. If you include it in an AI request or share an export, that copy leaves your device.
          </Body>
        </Section>


        <Section heading="Optional account identity">
          <Body>
            If you create or sign in to an account, the authentication service processes the email
            address or social sign-in identity needed to sign you in, plus a
            minimal profile record used for account state. UnAnonymous does not
            automatically copy your Journal, Mosaic, letters, held messages,
            Protect items, or private reflection text into that account.
          </Body>
          <Body>
            You can also continue using core reflection features without an
            account. Account deletion removes the UnAnonymous account and its minimal
            server profile; device-local content is separate and remains on the
            device until you delete or clear it yourself.
          </Body>
        </Section>

        <Section heading="What leaves your device">
          <Body>AI replies are disabled in this release. The following AI-processing description applies only if a processing connection is enabled in a future release.</Body>
          <Body>
            When you deliberately use an AI feature — including private Talk
            or an AI reflection from your Journal — the text you submit for
            that request and any context you choose to include leave your device so an AI response can be
            generated. The app does not automatically add a Talk session to
            your Journal; you choose whether to save it.
          </Body>
          <Body>
            Talk includes up to eight previous messages to keep the conversation coherent. Its Protect List option is off by default. Journal and grounding reflections may include Protect List labels, as disclosed on those screens. AI requests pass through the app’s backend to the configured processing provider. Before the request reaches that hosting gateway, UnAnonymous encrypts the reflection payload — including the random app-install identifier used for abuse limits — so ordinary gateway request-body views contain encrypted data rather than your reflection text. The backend decrypts the request only to apply the feature and send the needed content to the configured AI provider, and encrypts the AI reply before returning it through the gateway. This extra layer does not hide network metadata such as an IP address from hosting infrastructure, and the configured AI provider still receives the content needed to answer. For abuse limits, the backend converts the random install identifier and available network address information into keyed hashes for short-lived counters; the application database does not store the raw identifier, raw network address, reflection text, or AI reply in those counters. The AI endpoint does not need your UnAnonymous account token or email. The app does not intentionally add Talk transcripts to your private device records unless you choose to save something. Processing and hosting providers may retain or process data under their own terms, so do not assume zero retention.
          </Body>
          <Body>
            Uninstalling the app or clearing app data can remove device-local content, depending on your operating system and backup settings. It does not necessarily delete exported files, purchase records, account data, or information already processed by another service.
          </Body>
        </Section>

        <Section heading="Usage information and purchases">
          <Body>Optional first-party usage signals are off until you choose optional analytics. If enabled, the app uses the random app-install identifier with simple product events. These events do not include your reflection text. The separate use of that identifier for AI abuse limits occurs only when you choose an AI feature and does not turn optional analytics on. You can change the analytics choice in Settings or Manage Privacy. Changing the setting does not delete analytics events already received by the service.</Body>
          <Body>On Android, RevenueCat and Google Play process purchases and entitlement checks, including restore purchases. They receive purchase records and app identifiers needed to provide access. If you sign in, UnAnonymous may also give RevenueCat your internal account ID so Premium access can stay associated with that account across devices. Your journal, Mosaic, letters and other private reflection content are not sent to the billing service.</Body>
          <Body>Opening an external website, calling or messaging a contact, or sharing an export uses the service you select and its privacy terms. Safer Tonight does not automatically notify contacts or monitor your safety.</Body>
        </Section>
        <Section heading="Backups and deletion">
          <Body>Exports are readable files, not encrypted vault copies. Keep them private. On Android, UnAnonymous asks the operating system to exclude app data from cloud backup and device-to-device transfer; platform behavior can still vary by Android version and device maker. Removing data in the app does not remove files you exported, information already processed by other services, or Google Play purchase records. If you use an account, the Account screen includes a separate account-deletion control. It also requests removal of associated RevenueCat customer data; RevenueCat processes deletion asynchronously. Account deletion does not cancel a Google Play subscription. You can use the same account-deletion flow in your web browser without installing the Android app. Contact us with questions about service-held information.</Body>
        </Section>

        <Section heading="Adult audience">
          <Body>
            UnAnonymous is intended for adults aged 18 and older. Contact us if you believe information from someone under 18 has been submitted.
          </Body>
        </Section>

        <Section heading="Contact">
          <Body>
            <a
              href="mailto:rezzocandice@gmail.com"
              className="underline underline-offset-4"
              style={{ color: "var(--un-plum)" }}
            >
              rezzocandice@gmail.com
            </a>
          </Body>
        </Section>

        <hr
          className="my-10"
          style={{
            border: "none",
            height: 1,
            background: "rgba(91,57,84,0.12)",
          }}
        />

        <p
          className="text-[12px] leading-relaxed"
          style={{
            color: "var(--un-text-3)",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          <Link
            to="/"
            className="underline underline-offset-2"
            data-testid="privacy-back-link"
            style={{ color: "var(--un-text-2)" }}
          >
            Return to UnAnonymous
          </Link>
        </p>
      </motion.main>
    </div>
  );
}

function Section({ heading, children }) {
  return (
    <section className="mt-9">
      {heading && (
        <h2
          className="text-[19px] mb-3"
          style={{
            color: "var(--un-text-1)",
            fontFamily: "Manrope, sans-serif",
            fontWeight: 500,
          }}
        >
          {heading}
        </h2>
      )}
      {children}
    </section>
  );
}

function Body({ children }) {
  return (
    <p
      className="text-[16px] leading-[1.7] mt-3 first:mt-0"
      style={{
        color: "var(--un-text-2)",
        fontFamily: "Manrope, sans-serif",
      }}
    >
      {children}
    </p>
  );
}

function List({ items }) {
  return (
    <ul className="mt-3 mb-3 space-y-1.5">
      {items.map((it) => (
        <li
          key={it}
          className="text-[16px] leading-[1.7] pl-4 relative"
          style={{
            color: "var(--un-text-2)",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          <span
            aria-hidden
            className="absolute left-0 top-[0.75rem] w-1.5 h-1.5 rounded-full"
            style={{ background: "var(--un-plum)", opacity: 0.55 }}
          />
          {it}
        </li>
      ))}
    </ul>
  );
}
